import { uniq } from 'lodash';

import { composeMiddleware } from '../module';
import { localeActions } from '../state';

export type ILocaleLoader = (namespace: string, locale: string) => Promise<any>;

export const detectedLocale =
  typeof window !== undefined
    ? (navigator.languages && navigator.languages[0]) || // Chrome, FF, Safari
      (navigator as any).language || // MS Edge and IE 11
      Intl.DateTimeFormat().resolvedOptions().locale
    : 'en-US'; // fallback for node

export async function getDataForLocale(
  namespace,
  loader: ILocaleLoader,
  requestedLocale = detectedLocale,
  fallbackParam?: string
) {
  const fallbackLocale = fallbackParam || 'en-US';

  const requests = uniq([
    ...uniq([fallbackLocale.split('-')[0], fallbackLocale]),
    ...uniq([requestedLocale.split('-')[0], requestedLocale])
  ]);

  return Promise.all(requests.map(async (locale) => loader(namespace, locale).catch(() => null))).then((values) => {
    return values.reduce((result, value) => {
      if (!value) {
        // how does it react to failed requests, like a locale that doesn't exist?
        return result;
      }

      return Object.assign(result, value);
    }, {});
  });
}

export const localeLoaderMiddleware = (namespace: string, loader: ILocaleLoader) =>
  composeMiddleware(() => (next) => (action) => {
    if (action.type === localeActions.loadingLocale.actionType || action.type === 'APP_INITIALIZED') {
      next(localeActions.setCurrentLocale.create(detectedLocale));

      getDataForLocale(namespace, loader)
        .then((data) => {
          next(localeActions.loadedLocaleNamespace.create({ namespace, data }));
        })
        .catch((error) => {
          next(localeActions.loadLocaleNamespaceFailure.create({ namespace, error }));
        });
    }

    return next(action);
  });
